/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Box } from '@chakra-ui/react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { isCPF } from 'validation-br';
import { useFormValidationToast } from '../../../services/actionToast';
import { InputForm } from '../../inputs/forms/InputForm';
import { ImageInputForm } from '../../inputs/forms/ImageInputForm';
import { useFetch } from '../../../hooks/useFetch';
import { FormActions, FormRow, FormSection, FormShell } from '../../ui';
import { SelectInputForm } from '../../inputs/forms/SelectInputForm';
import { MaskedInputForm } from '../../inputs/forms/MaskedInputForm';

interface DecisionFormProps {
  defaultValues?: any;
}

interface FormInputsProps {
  name: string;
  value: string;
  placeholder?: string;
  label: string | undefined;
  yup: any;
}

export default function ProfileForm({ defaultValues = {} }: DecisionFormProps) {
  const validationToast = useFormValidationToast();
  const baseUrl = '/v1/client';
  const { requestApi, loading } = useFetch(baseUrl);
  const [images, setImages] = useState<any>('');
  const [isUpping, setIsUpping] = useState<any>('');

  const optionsGenre = [
    {
      label: 'Masculino',
      value: 'M',
    },
    {
      label: 'Feminino',
      value: 'F',
    },
  ];

  const yupschema: any = {};
  const defaultValuesSchema: any = {};

  const formInputs: FormInputsProps[] = [
    {
      name: 'name',
      value: defaultValues.name ?? '',
      placeholder: 'Nome',
      label: 'Nome',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'email',
      value: defaultValues.email ?? '',
      placeholder: 'Email',
      label: 'Email',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'cell_phone',
      value: defaultValues.cell_phone ?? '',
      placeholder: '(99) 99999-9999',
      label: 'Telefone',
      yup: Yup.string()
        .required('Este campo é obrigatório!')
        .matches(/^\([1-9]{2}\) (9[1-9])[0-9]{3}-[0-9]{4}/, 'Telefone inválido'),
    },
    {
      name: 'taxpayer',
      value: defaultValues.taxpayer ?? '',
      placeholder: '999.999.999-99',
      label: 'CPF',
      yup: Yup.string()
        .required('Este campo é obrigatório!')
        .test('taxpayer', 'CPF inválido', (val) => val !== undefined && isCPF(val)),
    },
    {
      name: 'genre',
      value: defaultValues?.genre
        ? optionsGenre.find((item) => item.value === defaultValues.genre)
        : null,
      placeholder: 'Gênero do usuário',
      label: 'Gênero',
      yup: Yup.mixed().required('Este campo é obrigatório!'),
    },
    {
      name: 'image',
      value: defaultValues.image?.medium ?? null,
      placeholder: '',
      label: 'Foto de perfil',
      yup: Yup.mixed().nullable(),
    },
  ];

  formInputs.forEach((object) => {
    yupschema[object.name] = object.yup;
    defaultValuesSchema[object.name] = object.value;
  });
  const schema = Yup.object().shape(yupschema);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValuesSchema,
  });

  const name = formInputs.find((field) => field.name === 'name');
  const email = formInputs.find((field) => field.name === 'email');
  const cellphone = formInputs.find((field) => field.name === 'cell_phone');
  const taxpayer = formInputs.find((field) => field.name === 'taxpayer');
  const genre = formInputs.find((field) => field.name === 'genre');
  const image = formInputs.find((field) => field.name === 'image');

  const onSubmit = async (values: any) => {
    const profileValues = { ...values, genre: values.genre.value };
    delete profileValues.image;
    const { status: resStatus, data } = await requestApi('put', profileValues);
    if ((resStatus === 200 || resStatus === 201) && images) {
      const apiUrlId = data?.data?.id;
      await requestApi('put', { file: images.toString() }, `/v1/admin/users/${apiUrlId}/images`);
    }
  };

  useEffect(() => {
    if (defaultValues.name) {
      reset(defaultValuesSchema);
    }
  }, [defaultValues]);

  return (
    <FormShell title="Perfil" description="Gerencie as informações exibidas em toda a plataforma.">
      <Box
        as="form"
        noValidate
        onSubmit={handleSubmit(onSubmit, validationToast)}
        display="flex"
        flexDirection="column"
        gap="16px"
      >
        <FormSection title="Informações pessoais">
          <ImageInputForm
            variant="avatar"
            avatarName={watch('name')}
            defaultVal={defaultValues.image}
            setImages={setImages}
            setIsUpping={setIsUpping}
            name={image?.name}
            label={image?.label}
            register={register}
            watch={watch}
            setValue={setValue}
            errors={errors}
          />
          <FormRow gridTemplateColumns={{ base: '1fr', md: '2fr 1fr 2fr' }}>
            <InputForm
              name={name?.name}
              label={name?.label}
              placeholder={name?.placeholder}
              register={register}
              errors={errors}
              isRequired
            />
            <SelectInputForm
              name={genre?.name}
              label={genre?.label}
              options={optionsGenre}
              isRequired
              placeholder={genre?.placeholder}
              control={control}
              errors={errors}
            />
            <MaskedInputForm
              name={taxpayer?.name}
              label={taxpayer?.label}
              placeholder={taxpayer?.placeholder}
              control={control}
              errors={errors}
              mask="999.999.999-99"
              isRequired
            />
          </FormRow>
        </FormSection>

        <FormSection title="Contato">
          <FormRow>
            <InputForm
              name={email?.name}
              label={email?.label}
              placeholder={email?.placeholder}
              register={register}
              errors={errors}
              isRequired
            />
            <MaskedInputForm
              name={cellphone?.name}
              label={cellphone?.label}
              placeholder={cellphone?.placeholder}
              control={control}
              errors={errors}
              mask="(99) 99999-9999"
              isRequired
            />
          </FormRow>
        </FormSection>

        <FormActions isLoading={isSubmitting || loading || isUpping} submitLabel="Salvar perfil" />
      </Box>
    </FormShell>
  );
}
